"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "energy-cost-calculator",
  "refrigerator-size-calculator",
  "refrigerator-lifespan-calculator",
  "cooking-temperature-converter",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Dual Compressor Refrigerator Calculator",
  category: "Energy & Cost" as const,
  mediumDescription: "Compare lifetime energy use and payback for a dual-compressor refrigerator vs a single-compressor model. Dual-compressor systems use roughly 20% less energy because the fridge and freezer cycle independently.",
  faqs: [
  {
    "question": "What is a dual-compressor refrigerator?",
    "answer": "Two independent sealed systems — one for the fresh-food compartment, one for the freezer. Each has its own compressor, evaporator, and thermostat, so they cycle on demand instead of cooling both compartments together."
  },
  {
    "question": "How much energy does dual compressor save?",
    "answer": "Roughly 15–25% over a comparable single-compressor model, because each compartment runs only when it needs cooling. Savings scale with cabinet size — bigger fridges benefit more."
  },
  {
    "question": "Is dual compressor worth the price premium?",
    "answer": "Premium is typically $300–$500 over a single-compressor model. With ~$25–$40/year in electricity savings on a 25 cu ft fridge, payback is usually 8–15 years — closer to 5–8 years on larger cabinets or higher electricity rates."
  },
  {
    "question": "Does dual compressor really keep odors out of fresh food?",
    "answer": "Yes. Single-compressor fridges share airflow between freezer and fridge, so freezer odors can transfer. Dual systems isolate airflow, preserving humidity in the fresh-food side and dry conditions in the freezer."
  },
  {
    "question": "Are dual-compressor fridges louder?",
    "answer": "Usually quieter. Two smaller compressors run less often than one large one cycling continuously, and they spend more time off."
  }
],
  relatedTools: ["energy-cost-calculator","refrigerator-size-calculator","appliance-energy-calculator"],
}

export function DualCompressorEfficiencyPageClient() {


  const [fridgeSize, setFridgeSize] = useState("25")
  const [usage, setUsage] = useState("moderate")

  // Calc logic carried over from the legacy page-client, with one change: the
  // input guard. It read `parseFloat(fridgeSize)` behind `if (!size)`, and
  // `!size` is false for every negative — so `-25` reached the arithmetic and
  // the page rendered "Payback in −30.8 years (10-yr savings $−130)". `0.0001`
  // passed the same guard and rendered "Payback in 7,692,307.7 years", because
  // a near-zero cabinet makes the annual saving a near-zero denominator.
  //
  // The floor and ceiling are the real-world range of a refrigerator cabinet,
  // which is what makes the near-zero case impossible rather than merely
  // positive. FRIDGE-384.
  const result = useMemo(() => {
    const size = readQuantity(fridgeSize, { min: 1, max: 100 })
    if (size === null) return null
    let singleKwh = size * 20
    if (usage === "light") singleKwh *= 0.9
    else if (usage === "heavy") singleKwh *= 1.15
    const dualKwh = singleKwh * 0.80
    const electricityRate = 0.13
    const singleCost = singleKwh * electricityRate
    const dualCost = dualKwh * electricityRate
    const annualSavings = singleCost - dualCost
    const tenYearSavings = annualSavings * 10
    const pricePremium = 400
    const paybackPeriod = pricePremium / annualSavings
    return {
      singleKwh: Math.round(singleKwh),
      dualKwh: Math.round(dualKwh),
      annualSavings: parseFloat(annualSavings.toFixed(2)),
      tenYearSavings: parseFloat(tenYearSavings.toFixed(2)),
      paybackPeriod: parseFloat(paybackPeriod.toFixed(1)),
    }
  }, [fridgeSize, usage])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="Two compressors,"
      heroTitleItalic="twenty"
      heroTitlePost="percent less energy."
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~20 sec","DOE EnergyGuide methodology"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "~20%", label: "Energy savings", note: "Dual vs single" },
            { value: "$300-500", label: "Price premium", note: "DOE typical" },
            { value: "$0.13", label: "EIA US rate", note: "2024 avg" },
            { value: "10 yr", label: "Lifetime view", note: "Total savings" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "dual-compressor-refrigerator-calculator")}
      cta={{
        title: "Ready to upgrade to a dual-compressor system?",
        body: "Browse French door fridges — the category most likely to ship with dual compressors.",
        buttonLabel: "Browse French door fridges",
        buttonHref: "/french-door-refrigerators",
      }}
    >
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
                Two inputs.
                <br />
                Real payback.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Cabinet size and usage drive single-compressor baseline; dual is a flat 20% reduction.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">Fridge size (cu ft)</label>
                  <div className="relative">
                    {/* min/max are the browser-level hint that pairs with the
                        readQuantity bounds above. They constrain the spinner and
                        mark a bad value :invalid — they do NOT stop a typed or
                        pasted one, which is why the compute guards too. */}
                    <input type="number" min={1} max={100} step="0.1" value={fridgeSize} onChange={(e) => setFridgeSize(e.target.value)} className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]" style={{ fontVariantNumeric: "tabular-nums" }} />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">cu ft</span>
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">Usage pattern</label>
                  <select value={usage} onChange={(e) => setUsage(e.target.value)} className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white">
                    <option value="light">Light (small household)</option>
                    <option value="moderate">Moderate (typical)</option>
                    <option value="heavy">Heavy (frequent open)</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Heavy use adds ~15% to single-compressor energy.</div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1" style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}>
                <div aria-hidden className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none" style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }} />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">Annual energy savings</div>
                  <div className="font-black" style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}>
                    {result ? `$${result.annualSavings.toLocaleString("en-US", { maximumFractionDigits: 0 })}` : "—"}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">/ yr</span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result ? `Payback in ${result.paybackPeriod} years (10-yr savings $${result.tenYearSavings.toLocaleString("en-US", { maximumFractionDigits: 0 })})` : "Enter cabinet size to see savings."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Single kWh/yr</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.singleKwh ?? "—"}</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Dual kWh/yr</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.dualKwh ?? "—"}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Shop the system"
          title="French door fridges with dual-compressor systems"
          description="The category where dual-compressor is most common — eye-level fresh food, independent freezer airflow."
          collectionType="french-door"
          fallbackCollectionType="counter-depth"
        />
      </section>

      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Under the hood</div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                DOE math. <em className="italic text-[#ff9951]">Real</em> bills.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Baseline kWh per cu ft from DOE EnergyGuide testing; dual-compressor reduction from manufacturer side-by-side specs.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                { n: "01", t: "Single-compressor baseline", d: "20 kWh per cubic foot per year, adjusted ±10–15% for usage. Matches DOE EnergyGuide averages for 25 cu ft cabinets." },
                { n: "02", t: "Dual-compressor reduction", d: "Multiply by 0.80 — independent cycling cuts compressor runtime by roughly 20% on average." },
                { n: "03", t: "Payback math", d: "$400 typical premium ÷ annual energy savings. Larger cabinets and higher electricity rates shorten the payback period." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900">
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">{s.n}</div>
                  <div>
                    <h3 className="text-gray-900" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Counter-depth fridges with dual systems"
          description="Counter-depth French doors are common dual-compressor flagships — flush install, premium efficiency."
          collectionType="counter-depth"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
